import React, { useState } from "react";
import {
  Box, Typography, Button, Chip, CircularProgress,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import VerifiedIcon from "@mui/icons-material/Verified";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Link } from "react-router-dom";
import { theme } from "../../theme/colors";
import { PARTY_COLORS, PARTY_LABELS } from "../../utils/discoverApi";
import { compactMoney } from "../../utils/fecApi";
import NoCivixAccountModal from "./NoCivixAccountModal";

export default function CandidateResultCard({
  candidate,
  onCardClick,
  onFollowToggle,
  isLoggedIn = false,
}) {
  const [isFollowing, setIsFollowing] = useState(candidate.viewerIsFollowing || false);
  const [isLoading, setIsLoading] = useState(false);
  const [showNoAccountModal, setShowNoAccountModal] = useState(false);

  const initials = candidate.displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const partyColor = PARTY_COLORS[candidate.party] || "#94a3b8";
  const partyLabel = PARTY_LABELS[candidate.party] || candidate.party || "Unknown";
  const totalRaised = compactMoney(candidate.totalRaised);
  const hasAccount = candidate.hasCivixThreadAccount === true;
  const detailHref = candidate.id ? `/candidates/${candidate.id}` : "/discover";

  const handleFollowPress = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) return;
    if (!hasAccount) {
      setShowNoAccountModal(true);
      return;
    }
    setIsLoading(true);
    try {
      const userIdToFollow = candidate.civixThreadUserId || candidate.id;
      await onFollowToggle(userIdToFollow, !isFollowing);
      setIsFollowing(!isFollowing);
    } catch {
      /* handled upstream */
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardClick = (e) => {
    if (onCardClick) {
      e.preventDefault();
      onCardClick(candidate);
    }
  };

  return (
    <>
      <Box
        component={Link}
        to={detailHref}
        onClick={handleCardClick}
        state={{ totalRaised: candidate.totalRaised }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2.5,
          textDecoration: "none",
          color: "inherit",
          cursor: "pointer",
          transition: "background 0.15s",
          "&:hover": { bgcolor: "#f8fafc" },
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: "50%",
            border: `2px solid ${theme.primary}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              bgcolor: `${theme.primary}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: 17, color: theme.primary }}>
              {initials}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 17,
                color: "#0f172a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {candidate.displayName}
            </Typography>
            {hasAccount && (
              <Chip
                size="small"
                icon={<AccountCircleIcon sx={{ fontSize: 12 }} />}
                label="On CivixThread"
                sx={{
                  height: 22,
                  fontSize: 10,
                  fontWeight: 600,
                  bgcolor: `${theme.primary}15`,
                  color: theme.primary,
                  "& .MuiChip-icon": { color: theme.primary },
                }}
              />
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: theme.primary }}>
              {candidate.office}
            </Typography>
            {candidate.districtLabel && (
              <>
                <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "#94a3b8" }} />
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#000" }}>
                  {candidate.districtLabel}
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
            <Chip
              size="small"
              label={partyLabel}
              sx={{
                height: 22,
                fontSize: 12,
                fontWeight: 600,
                bgcolor: `${partyColor}20`,
                color: "#000",
                "& .MuiChip-label": { px: 1.5 },
              }}
              icon={
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: partyColor, ml: "6px !important" }} />
              }
            />
            {candidate.incumbent && (
              <Chip
                size="small"
                icon={<VerifiedIcon sx={{ fontSize: 12 }} />}
                label="Incumbent"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 600,
                  bgcolor: `${theme.primary}15`,
                  color: theme.primary,
                  "& .MuiChip-icon": { color: theme.primary },
                }}
              />
            )}
            {candidate.openSeat && (
              <Chip
                size="small"
                label="Open Seat"
                sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#fef3c7", color: "#92400e" }}
              />
            )}
          </Box>

          {totalRaised && totalRaised !== "—" && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "#f0fdf4",
                px: 1.5,
                py: 0.5,
                borderRadius: "8px",
              }}
            >
              <AttachMoneyIcon sx={{ fontSize: 14, color: theme.primary }} />
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#334155" }}>
                Total Raised:
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.primary }}>
                {totalRaised}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {hasAccount && (
            <Button
              variant={isFollowing ? "outlined" : "contained"}
              size="small"
              onClick={handleFollowPress}
              disabled={isLoading || !isLoggedIn}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
                minWidth: 90,
                height: 36,
                ...(isFollowing
                  ? {
                      borderColor: "#e2e8f0",
                      color: "#334155",
                      bgcolor: "#f8fafc",
                      "&:hover": { bgcolor: "#f1f5f9", borderColor: "#cbd5e1" },
                    }
                  : {
                      background: theme.gradient,
                      color: "#fff",
                      "&:hover": { opacity: 0.9, background: theme.gradient },
                    }),
              }}
            >
              {isLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : isFollowing ? (
                "Following"
              ) : (
                "Follow"
              )}
            </Button>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: theme.gradient,
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 18, color: "#fff" }} />
          </Box>
        </Box>
      </Box>

      <NoCivixAccountModal
        open={showNoAccountModal}
        onClose={() => setShowNoAccountModal(false)}
        displayName={candidate.displayName}
      />
    </>
  );
}
